/**
 * Pure helpers behind the global entity search.
 *
 * The index is built from the distributions tree that already lives in Redux
 * (hydrated from the on-disk cache at startup, then refreshed from the API),
 * so searching never issues a request and works offline.
 */

const {
  ENTITY_TYPES,
  ENTITY_TYPE_ORDER,
  SENSOR_TYPE_LABELS,
  SENSOR_MANUFACTURER_LABELS,
  ARCHIVED_FIELD_STATE,
  MATCH_RANK,
  WORD_SEPARATORS,
  MAX_RESULTS,
  MAX_RECENT_FIELDS,
  FIELD_VIEW_PATH,
  FARM_VIEW_PATH,
  MAIN_SCREEN_PATHS,
} = require('./GlobalSearch.types');

const EMPTY_INDEX = [];
const EMPTY_RESULT = { results: EMPTY_INDEX, totalMatches: 0 };

const toHaystack = (values) =>
  values
    .filter((value) => typeof value === 'string' && value.length > 0)
    .map((value) => value.toLowerCase());

// Every entry carries its ancestors so a result can be rendered and navigated
// to without walking the tree again. `searchableValues` defaults to the id and
// the name; pass it explicitly when an entity is looked up by something else.
const createEntry = (entry, searchableValues) => ({
  ...entry,
  haystack: toHaystack(searchableValues ?? [entry.id, entry.name]),
});

// Sensors are matched on name and transmitter id only — never on the sensor's
// own id (its serial), which is not shown in the results either, so matching on
// it would surface rows with no visible reason for being there.
const collectSensorEntries = (field, context, entries) => {
  const sensors = field.sensors ?? {};
  for (const sensorKey in sensors) {
    const sensor = sensors[sensorKey];
    const sensorId = sensor?.id ?? sensorKey;
    const sensorEntry = {
      key: `${ENTITY_TYPES.SENSOR}:${context.fieldId}:${sensorId}`,
      type: ENTITY_TYPES.SENSOR,
      id: sensorId,
      name: sensor?.name ?? '',
      transmitterId: sensor?.transmitter_id ?? '',
      sensorType: sensor?.type ?? '',
      manufacturer: sensor?.manufacturer ?? '',
      ...context,
    };
    entries.push(
      createEntry(sensorEntry, [sensorEntry.name, sensorEntry.transmitterId]),
    );
  }
};

const collectFieldEntries = (farmingUnit, context, entries) => {
  const fields = farmingUnit.fields ?? {};
  for (const fieldKey in fields) {
    const field = fields[fieldKey];
    if (field?.settings?.field_state === ARCHIVED_FIELD_STATE) {
      continue;
    }
    const fieldContext = {
      ...context,
      fieldId: field?.id ?? fieldKey,
      fieldName: field?.name ?? '',
    };
    entries.push(
      createEntry({
        key: `${ENTITY_TYPES.FIELD}:${fieldContext.fieldId}`,
        type: ENTITY_TYPES.FIELD,
        id: fieldContext.fieldId,
        name: fieldContext.fieldName,
        ...fieldContext,
      }),
    );
    collectSensorEntries(field, fieldContext, entries);
  }
};

const collectFarmingUnitEntries = (distribution, context, entries) => {
  const farmingUnits = distribution.farming_units ?? {};
  for (const farmingUnitKey in farmingUnits) {
    const farmingUnit = farmingUnits[farmingUnitKey];
    const farmingUnitContext = {
      ...context,
      farmingUnitId: farmingUnit?.id ?? farmingUnitKey,
      farmingUnitName: farmingUnit?.name ?? '',
    };
    entries.push(
      createEntry({
        key: `${ENTITY_TYPES.FARMING_UNIT}:${farmingUnitContext.farmingUnitId}`,
        type: ENTITY_TYPES.FARMING_UNIT,
        id: farmingUnitContext.farmingUnitId,
        name: farmingUnitContext.farmingUnitName,
        ...farmingUnitContext,
      }),
    );
    collectFieldEntries(farmingUnit ?? {}, farmingUnitContext, entries);
  }
};

/**
 * Flattens the distributions tree into a searchable list.
 *
 * @param {object} distributions - `state.distribution_data.distributions`
 *   (the `Distributions` model, i.e. `{ distributions: { [id]: Distribution } }`).
 * @returns {Array<object>} one entry per distribution, farming unit,
 *   active field and field sensor.
 */
const buildSearchIndex = (distributions) => {
  const allDistributions = distributions?.distributions;
  if (!allDistributions) {
    return EMPTY_INDEX;
  }

  const entries = [];
  for (const distributionKey in allDistributions) {
    const distribution = allDistributions[distributionKey];
    const context = {
      distributionId: distribution?.id ?? distributionKey,
      distributionName: distribution?.name ?? '',
    };
    entries.push(
      createEntry({
        key: `${ENTITY_TYPES.DISTRIBUTION}:${context.distributionId}`,
        type: ENTITY_TYPES.DISTRIBUTION,
        id: context.distributionId,
        name: context.distributionName,
        ...context,
      }),
    );
    collectFarmingUnitEntries(distribution ?? {}, context, entries);
  }
  return entries;
};

const tokenize = (query) =>
  typeof query === 'string'
    ? query.toLowerCase().trim().split(/\s+/).filter(Boolean)
    : [];

const rankToken = (text, token) => {
  if (text === token) {
    return MATCH_RANK.EXACT;
  }
  const index = text.indexOf(token);
  if (index === -1) {
    return MATCH_RANK.NO_MATCH;
  }
  if (index === 0) {
    return MATCH_RANK.PREFIX;
  }
  return WORD_SEPARATORS.includes(text[index - 1])
    ? MATCH_RANK.WORD_PREFIX
    : MATCH_RANK.SUBSTRING;
};

const rankTokenAgainstEntry = (haystack, token) => {
  let best = MATCH_RANK.NO_MATCH;
  for (const text of haystack) {
    best = Math.min(best, rankToken(text, token));
    if (best === MATCH_RANK.EXACT) {
      break;
    }
  }
  return best;
};

// Every token must hit something; the entry is scored by its weakest token so
// that "north 12" ranks below an entry where both tokens match strongly.
const rankEntry = (entry, tokens) => {
  let worst = MATCH_RANK.EXACT;
  for (const token of tokens) {
    const rank = rankTokenAgainstEntry(entry.haystack, token);
    if (rank === MATCH_RANK.NO_MATCH) {
      return MATCH_RANK.NO_MATCH;
    }
    worst = Math.max(worst, rank);
  }
  return worst;
};

const compareMatches = (a, b) =>
  a.rank - b.rank ||
  ENTITY_TYPE_ORDER[a.entry.type] - ENTITY_TYPE_ORDER[b.entry.type] ||
  (a.entry.name || a.entry.id || '').localeCompare(
    b.entry.name || b.entry.id || '',
  );

/**
 * Ranks index entries against a query.
 *
 * @returns {{results: Array<object>, totalMatches: number}} `results` is capped
 *   at `maxResults`; `totalMatches` counts every match so the UI can say how
 *   many were left out.
 */
const searchEntities = (index, query, maxResults = MAX_RESULTS) => {
  const tokens = tokenize(query);
  if (tokens.length === 0 || !index?.length) {
    return EMPTY_RESULT;
  }

  const matches = [];
  for (const entry of index) {
    const rank = rankEntry(entry, tokens);
    if (rank !== MATCH_RANK.NO_MATCH) {
      matches.push({ entry, rank });
    }
  }
  matches.sort(compareMatches);

  return {
    results: matches.slice(0, maxResults).map((match) => match.entry),
    totalMatches: matches.length,
  };
};

// Field ids arrive as object keys (strings) in some places and as `field.id`
// (sometimes a number) in others, so everything the recents touch is compared
// as a string.
const toRecentFieldId = (fieldId) =>
  fieldId === null || fieldId === undefined ? '' : String(fieldId);

const normalizeRecentFieldIds = (recentIds) =>
  Array.isArray(recentIds) ? recentIds : EMPTY_INDEX;

/**
 * Puts a visited field at the front of the recents, most recent first.
 *
 * Re-visiting a field moves it up rather than adding a second row, and the
 * list is capped at `maxRecent`. Returns the list **unchanged (same
 * reference)** when the field is already at the front, so the caller can skip
 * a redundant write — the app re-selects the current field on plenty of
 * unrelated renders.
 *
 * @param {Array<string>} recentIds - current list, most recent first
 * @param {string|number} fieldId - the field just visited
 * @returns {Array<string>}
 */
const addRecentFieldId = (
  recentIds,
  fieldId,
  maxRecent = MAX_RECENT_FIELDS,
) => {
  const current = normalizeRecentFieldIds(recentIds);
  const id = toRecentFieldId(fieldId);
  if (!id) {
    return current;
  }
  if (current[0] === id && current.length <= maxRecent) {
    return current;
  }
  return [id, ...current.filter((value) => toRecentFieldId(value) !== id)]
    .map(toRecentFieldId)
    .slice(0, maxRecent);
};

/**
 * Turns stored field ids into renderable index entries, most recent first.
 *
 * Only ids are stored, never a snapshot of the field: resolving them against
 * the live index means a renamed field shows its new name, and a field that
 * has since been deleted or archived (archived fields are not indexed) simply
 * drops out instead of offering a row that navigates nowhere.
 *
 * @param {Array<object>} index - from buildSearchIndex
 * @param {Array<string>} recentIds - most recent first
 * @returns {Array<object>} index entries, in the same order
 */
const resolveRecentFieldEntries = (index, recentIds) => {
  const ids = normalizeRecentFieldIds(recentIds);
  if (ids.length === 0 || !index?.length) {
    return EMPTY_INDEX;
  }

  const fieldsById = new Map();
  for (const entry of index) {
    if (entry.type === ENTITY_TYPES.FIELD) {
      fieldsById.set(toRecentFieldId(entry.id), entry);
    }
  }
  return ids
    .map((id) => fieldsById.get(toRecentFieldId(id)))
    .filter((entry) => entry !== undefined);
};

/**
 * The ancestors of an entry, outermost first — rendered under the result name.
 */
const buildBreadcrumb = (entry) => {
  const ancestors = {
    [ENTITY_TYPES.DISTRIBUTION]: [],
    [ENTITY_TYPES.FARMING_UNIT]: [entry.distributionName],
    [ENTITY_TYPES.FIELD]: [entry.distributionName, entry.farmingUnitName],
    [ENTITY_TYPES.SENSOR]: [
      entry.distributionName,
      entry.farmingUnitName,
      entry.fieldName,
    ],
  };
  return (ancestors[entry.type] ?? []).filter(Boolean);
};

const isFieldLevelEntry = (entry) =>
  entry.type === ENTITY_TYPES.FIELD || entry.type === ENTITY_TYPES.SENSOR;

/**
 * Where a result should take the user, and what the destination screen has to
 * do beyond the Redux selection.
 *
 * Field and sensor results resolve to a field, which both the field view and
 * the Farm View can show — so a user already on one of those stays where they
 * are rather than being thrown across the app. The Farm View keeps its
 * selection in component state, so it also needs an explicit focus request;
 * for a sensor that request opens the Field Details tab, where the
 * water-potential graph lives.
 *
 * Distribution and farming unit results need no field, so they only move a
 * user who is on a screen that shows neither.
 *
 * @returns {{path: string|null, focusField: object|null}} `path` is null when
 *   the user should stay put.
 */
const getSearchNavigationPlan = (entry, pathname) => {
  if (!isFieldLevelEntry(entry)) {
    return {
      path: MAIN_SCREEN_PATHS.includes(pathname) ? null : FIELD_VIEW_PATH,
      focusField: null,
    };
  }
  if (pathname === FARM_VIEW_PATH) {
    return {
      path: null,
      focusField: {
        fieldId: entry.fieldId,
        farmingUnitId: entry.farmingUnitId,
        openDetails: entry.type === ENTITY_TYPES.SENSOR,
      },
    };
  }
  return {
    path: pathname === FIELD_VIEW_PATH ? null : FIELD_VIEW_PATH,
    focusField: null,
  };
};

/**
 * How a result is labelled: sensors lead with their name and fall back to the
 * transmitter id (never the serial), everything else leads with its name and
 * falls back to its id. `detail` is omitted when it would repeat the title.
 */
const getEntryDisplay = (entry) => {
  if (entry.type !== ENTITY_TYPES.SENSOR) {
    return {
      title: entry.name || entry.id,
      detail: entry.name ? entry.id : '',
    };
  }
  const title = entry.name || entry.transmitterId;
  return {
    title,
    detail: title === entry.transmitterId ? '' : entry.transmitterId,
    typeLabel: getSensorTypeLabel(entry),
  };
};

/**
 * The sensor's product name, or '' when neither the type nor the manufacturer
 * is one we have a name for.
 */
const getSensorTypeLabel = (entry) =>
  SENSOR_TYPE_LABELS[entry.sensorType] ??
  SENSOR_MANUFACTURER_LABELS[entry.manufacturer] ??
  '';

/**
 * How much is searchable, per entity type — reported when the box is opened so
 * usage can be read against the size of the user's world.
 */
const countEntriesByType = (index) => {
  const counts = {
    searchable_distributions: 0,
    searchable_farming_units: 0,
    searchable_fields: 0,
    searchable_sensors: 0,
  };
  const key = {
    [ENTITY_TYPES.DISTRIBUTION]: 'searchable_distributions',
    [ENTITY_TYPES.FARMING_UNIT]: 'searchable_farming_units',
    [ENTITY_TYPES.FIELD]: 'searchable_fields',
    [ENTITY_TYPES.SENSOR]: 'searchable_sensors',
  };
  (index ?? []).forEach((entry) => {
    counts[key[entry.type]] += 1;
  });
  return counts;
};

/**
 * Mixpanel properties for a chosen result. Title Case event names and
 * snake_case properties, per the repo convention.
 *
 * `entity_type` is always one of the four ENTITY_TYPES values — distribution,
 * farming_unit, field, sensor — so the funnel can be split by what people
 * actually look for. The ancestor ids/names are always present (a sensor hit
 * carries its field, farming unit and distribution), which is what makes it
 * possible to ask "which farms are people searching into" without joining
 * anything.
 *
 * `result_source` says whether the row came from the search itself or from the
 * recently-visited-fields list the box shows before anything is typed, so the
 * two can be told apart in the funnel.
 *
 * @param {object} entry - the chosen index entry
 * @param {object} context - query, position, and how it was chosen
 */
const buildResultAnalytics = (entry, context) => ({
  entity_type: entry.type,
  entity_id: entry.id,
  entity_name: entry.name || '',
  distribution_id: entry.distributionId || '',
  distribution_name: entry.distributionName || '',
  farming_unit_id: entry.farmingUnitId || '',
  farming_unit_name: entry.farmingUnitName || '',
  field_id: entry.fieldId || '',
  field_name: entry.fieldName || '',
  // Sensors only; empty for every other entity type.
  transmitter_id: entry.transmitterId || '',
  sensor_type: entry.sensorType || '',
  // 'search' | 'recent' — which list the row was chosen from.
  result_source: context.resultSource,
  selection_method: context.selectionMethod,
  result_index: context.resultIndex,
  result_count: context.resultCount,
  query_length: context.queryLength,
  origin_path: context.originPath,
  navigated_to: context.navigatedTo || '',
});

const SEARCH_SHORTCUT_KEY = 'k';
const SEARCH_SHORTCUT_CODE = 'KeyK';
const LATIN_LETTER = /^[a-z]$/i;

/**
 * True for Cmd/Ctrl + K on any keyboard layout.
 *
 * `event.key` is the layout's character, so on a Hebrew layout the K key
 * reports `ל` and a key-only check never fires. For non-Latin layouts we fall
 * back to `event.code`, the physical key. The fallback is limited to them on
 * purpose: on Latin layouts that move letters around (Dvorak, AZERTY) the
 * physical K key is a different letter, and the user expects the K they see.
 */
const isSearchShortcut = (event) => {
  if (!event.metaKey && !event.ctrlKey) {
    return false;
  }
  const key = event.key?.toLowerCase();
  if (key === SEARCH_SHORTCUT_KEY) {
    return true;
  }
  return !LATIN_LETTER.test(key ?? '') && event.code === SEARCH_SHORTCUT_CODE;
};

module.exports = {
  buildSearchIndex,
  searchEntities,
  addRecentFieldId,
  resolveRecentFieldEntries,
  buildBreadcrumb,
  getSearchNavigationPlan,
  buildResultAnalytics,
  countEntriesByType,
  getEntryDisplay,
  getSensorTypeLabel,
  tokenize,
  rankToken,
  isSearchShortcut,
};
